import type {
  ActionItemView,
  ActionStatus,
  AdminTeamView,
  AdminUserView,
  RetroPhase,
  RetroSnapshot,
  TeamRole,
  TeamView,
  TemplateColumnSeed,
  UserView,
} from '@retro/shared';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  // Declared on every mutation, not only those carrying a body. The server's CSRF
  // guard requires it — an HTML form cannot set it — and a bodyless POST such as
  // logout is still a mutation. Keying this off the body instead is what silently
  // broke signing out.
  const isMutation = !SAFE_METHODS.has((init.method ?? 'GET').toUpperCase());

  const response = await fetch(`/api${path}`, {
    ...init,
    // Session lives in an httpOnly cookie, so every call must include it.
    credentials: 'include',
    headers: {
      ...(isMutation ? { 'content-type': 'application/json' } : {}),
      ...init.headers,
    },
  });

  if (response.status === 204) return undefined as T;

  const payload = await response.json().catch(() => null);

  if (!response.ok) {
    const error = (payload as { error?: { code?: string; message?: string } } | null)?.error;
    throw new ApiError(
      response.status,
      error?.code ?? 'INTERNAL',
      error?.message ?? 'Something went wrong.',
    );
  }

  return payload as T;
}

const post = <T>(path: string, body?: unknown) =>
  request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) });

const patch = <T>(path: string, body: unknown) =>
  request<T>(path, { method: 'PATCH', body: JSON.stringify(body) });

/* ---------------------------------- types --------------------------------- */

export type TemplateSummary = {
  id: string;
  key: string | null;
  name: string;
  description: string;
  category: 'CLASSIC' | 'THEMED' | 'CUSTOM';
  isCustom: boolean;
  columns: TemplateColumnSeed[];
};

/** The editable part of a template — what the builder submits. */
export type TemplateDraft = {
  name: string;
  description: string;
  columns: TemplateColumnSeed[];
};

export type RetroSummary = {
  id: string;
  name: string;
  phase: RetroPhase;
  templateName: string;
  createdAt: string;
  closedAt: string | null;
  commentCount: number;
  participantCount: number;
  actionItemCount: number;
};

export type TeamDetail = TeamView & {
  inviteToken: string | null;
  members: { userId: string; name: string; email: string; avatar: string; role: TeamRole }[];
  topics: { id: string; name: string; color: string; isBuiltin: boolean }[];
};

export type InvitePreview =
  | {
      kind: 'RETRO';
      mode: 'MEMBER' | 'GUEST';
      retro: { id: string; name: string; phase: RetroPhase; templateName: string; teamName: string };
    }
  | { kind: 'TEAM'; team: { id: string; name: string } };

/* ----------------------------------- api ---------------------------------- */

export const api = {
  auth: {
    me: () => request<{ user: UserView }>('/auth/me'),
    register: (input: {
      name: string;
      email: string;
      password: string;
      // Present when following a team invite, which is what allows signing up on
      // an instance that has closed open registration.
      inviteToken?: string;
    }) => post<{ user: UserView }>('/auth/register', input),
    login: (input: { email: string; password: string }) => post<{ user: UserView }>('/auth/login', input),
    logout: () => post<void>('/auth/logout'),
    updateProfile: (input: { name: string; avatar: string }) =>
      patch<{ user: UserView }>('/auth/me', input),
    changePassword: (input: { currentPassword: string; newPassword: string }) =>
      post<void>('/auth/me/password', input),
    checkResetToken: (token: string) => request<{ email: string }>(`/auth/reset-password/${token}`),
    resetPassword: (token: string, newPassword: string) =>
      post<void>('/auth/reset-password', { token, newPassword }),
  },

  teams: {
    list: () => request<{ teams: TeamView[] }>('/teams'),
    create: (name: string) => post<{ team: TeamView }>('/teams', { name }),
    get: (teamId: string) => request<{ team: TeamDetail }>(`/teams/${teamId}`),
    rename: (teamId: string, name: string) => patch<void>(`/teams/${teamId}`, { name }),
    // Mint a fresh team invite token. The old link stops working immediately;
    // use this when a previous link has been shared more widely than intended.
    rotateInvite: (teamId: string) =>
      post<{ inviteLink: string }>(`/teams/${teamId}/invite/rotate`),
    setRole: (teamId: string, userId: string, role: TeamRole) =>
      patch<void>(`/teams/${teamId}/members/${userId}`, { role }),
    removeMember: (teamId: string, userId: string) =>
      request<void>(`/teams/${teamId}/members/${userId}`, { method: 'DELETE' }),
    retros: (teamId: string) => request<{ retros: RetroSummary[] }>(`/teams/${teamId}/retros`),
    actions: (teamId: string) => request<{ actionItems: ActionItemView[] }>(`/teams/${teamId}/actions`),
    setActionStatus: (teamId: string, actionItemId: string, status: ActionStatus) =>
      patch<void>(`/teams/${teamId}/actions/${actionItemId}`, { status }),
  },

  config: {
    get: () => request<{ allowRegistration: boolean }>('/config'),
  },

  admin: {
    users: () => request<{ users: AdminUserView[] }>('/admin/users'),
    setAdmin: (userId: string, isAdmin: boolean) =>
      patch<{ ok: true }>(`/admin/users/${userId}`, { isAdmin }),
    // Soft delete: the account stops working, its contributions to past
    // retrospectives stay put. See User.deletedAt in schema.prisma.
    deleteUser: (userId: string) =>
      request<{ ok: true }>(`/admin/users/${userId}`, { method: 'DELETE' }),
    restoreUser: (userId: string) =>
      post<{ ok: true }>(`/admin/users/${userId}/restore`),
    // Single-use, 1-hour password reset link. The admin passes it to the person
    // out of band; the server sends no email.
    createResetLink: (userId: string) =>
      post<{ resetLink: string; expiresAt: string }>(`/admin/users/${userId}/reset-link`),
    // Cancels a link already handed out — see the endpoint's comment for why
    // minting a replacement is not the same thing.
    revokeResetLink: (userId: string) =>
      request<{ revoked: number }>(`/admin/users/${userId}/reset-link`, { method: 'DELETE' }),
    teams: () => request<{ teams: AdminTeamView[] }>('/admin/teams'),
    deleteTeam: (teamId: string) =>
      request<{ ok: true }>(`/admin/teams/${teamId}`, { method: 'DELETE' }),
    joinTeam: (teamId: string) => post<{ ok: true }>(`/admin/teams/${teamId}/join`),
  },

  templates: {
    list: (teamId: string) => request<{ templates: TemplateSummary[] }>(`/templates?teamId=${teamId}`),
    create: (input: TemplateDraft & { teamId: string }) =>
      post<{ template: TemplateSummary }>('/templates', input),
    update: (templateId: string, input: TemplateDraft) =>
      patch<{ template: TemplateSummary }>(`/templates/${templateId}`, input),
    remove: (templateId: string) => request<void>(`/templates/${templateId}`, { method: 'DELETE' }),
  },

  retros: {
    create: (input: { teamId: string; templateId: string; name?: string }) =>
      post<{ retroId: string }>('/retros', input),
    join: (retroId: string) => post<{ participantId: string }>(`/retros/${retroId}/join`),
    snapshot: (retroId: string) => request<{ snapshot: RetroSnapshot }>(`/retros/${retroId}/snapshot`),
    invites: (retroId: string) =>
      request<{ memberLink: string; guestLink: string }>(`/retros/${retroId}/invites`),
    // Mint fresh member and guest tokens. Returns both new links — the
    // facilitator hands them out, replacing any earlier copies in chat.
    rotateInvites: (retroId: string) =>
      post<{ memberLink: string; guestLink: string }>(`/retros/${retroId}/invites/rotate`),
    // Closed retros only, facilitators and admins only. Hard delete: the comments,
    // votes, ROTI responses and action items go with it.
    remove: (retroId: string) => request<void>(`/retros/${retroId}`, { method: 'DELETE' }),
  },

  invites: {
    preview: (token: string) => request<InvitePreview>(`/invites/${token}`),
    redeem: (token: string, name?: string) =>
      post<{ kind: 'RETRO'; retroId: string; participantId: string } | { kind: 'TEAM'; teamId: string }>(
        `/invites/${token}`,
        name ? { name } : {},
      ),
  },
};
